import { useEffect, useState } from 'react'
import { SelectField } from '@susu/ui/components'
import { useI18N } from '@susu/i18n/client'
import type { Environment } from '../../lib/config'
import { fetchLanguages, type Language } from '../../lib/languages.api'

interface LanguageDropdownProps {
  value: string
  onChange: (value: string) => void
  env?: Environment
  'data-testid'?: string
}

export function LanguageDropdown({
  value,
  onChange,
  env = 'tst',
  'data-testid': testId,
}: LanguageDropdownProps) {
  const { t } = useI18N()
  const [languages, setLanguages] = useState<Language[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const response = await fetchLanguages(env)
        setLanguages(response.languages)
        setError(null)

        const isValueInList = response.languages.some(
          (lang) => lang.code === value,
        )
        if (!isValueInList) {
          onChange('en')
        }
      } catch (error) {
        console.error('Failed to fetch languages:', error)
        setError(t('error.loadLanguages'))
      } finally {
        setLoading(false)
      }
    }

    loadLanguages()
  }, [env])

  // Transform languages to SelectField options format
  const options = languages.map((lang) => ({
    value: lang.code,
    label: lang.localLabel || lang.label,
  }))

  // Show error message if languages failed to load
  if (error && !loading) {
    return (
      <div className="w-full">
        <div className="text-sm text-text-error py-8">{error}</div>
      </div>
    )
  }

  return (
    <div data-testid={testId}>
      <SelectField
        id="language-select"
        name="language"
        label={loading ? t('loading.general') : t('sections.language.title')}
        value={value}
        options={options}
        onChange={onChange}
        disabled={loading || options.length === 0}
        data-testid="language-dropdown"
        error={
          options.length === 0 && !loading
            ? 'No languages available'
            : undefined
        }
      />
    </div>
  )
}
